import React, { useEffect, useMemo, useState } from 'react';
import { Button, Popconfirm, Form, Input, Tooltip, Upload, Alert } from 'antd';
import { CloseOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, FileExcelOutlined, HeartOutlined, IdcardOutlined, SaveOutlined, UserOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { Trans, useTranslation } from 'react-i18next';
import { T, H_TITLE } from './declarationTheme';
import { TYPES_VICTIME } from '../../../variables/constants';
import { Field, Grid2, Grid3, Switch } from './fields';
import CBInputNumber from '../../../components/CBInputNumber';
import VictimeModal from '../../../components/VictimeModal';
import { RequiredLegend } from '../../../components/FieldLabel';
import { Panel, EmptyState, PanelFooterNote, ROW_BG, ROW_BORDER } from './Panel';
import { telechargerModeleVictimes, lireVictimesExcel } from './importVictimesExcel';
import { memePersonneAutreRole, confirmerDoublonPersonne } from '../doublonPersonne';

/* ─────────────────────────────────────────────────────────────────────────
   Étape 5 - Victimes (côté assuré)

   Deux temps, dans cet ordre :
     1. le BILAN chiffré - toujours saisi, c'est ce que le déclarant connaît
        immédiatement (combien de blessés, combien de décès) ;
     2. les FICHES nominatives - ajoutées au fur et à mesure des identités
        connues, souvent à réception du PV.

   Il n'y a donc plus de bascule « nombre seul / fiches détaillées » :
   `victimesNonIdentifiees` se déduit de l'état réel de la saisie.
   ───────────────────────────────────────────────────────────────────────── */

const num = (v) => Number(v) || 0;

export default function StepVictimes({ form, victimes, setVictimes, adversaires = [], pays = [], bilanInitial = null }) {
  const { t } = useTranslation('declaration');
  const [modal, setModal] = useState({ open: false, editing: null, index: -1 });
  const [suggestionRejetee, setSuggestionRejetee] = useState(false);
  const [erreursImport, setErreursImport] = useState([]);

  const blesses = Form.useWatch('nombreBlessesAttendu', form);
  const decedes = Form.useWatch('nombreDecedesAttendu', form);

  const typeDommage = Form.useWatch('typeDommage', form);
  const conducteurEstAssure = Form.useWatch('conducteurEstAssure', form);
  const assureNom = Form.useWatch('assureNom', form);
  const assurePrenom = Form.useWatch('assurePrenom', form);
  const conducteurNom = Form.useWatch('conducteurNom', form);
  const conducteurPrenom = Form.useWatch('conducteurPrenom', form);

  const totalBlesses = num(blesses);
  const totalVictimes = totalBlesses + num(decedes);

  /* Tant qu'aucun des quatre compteurs n'a été observé, le formulaire n'a pas
     encore propagé ses valeurs (premier rendu, ou restauration d'un dossier en
     modification) : dériver maintenant écraserait le bilan chargé par des zéros. */
  const bilanNonInitialise = blesses === undefined && decedes === undefined;

  /* Les agrégats attendus par l'API se déduisent des quatre compteurs.
     `victimesNonIdentifiees` = un bilan annoncé sans aucune fiche nominative. */
  useEffect(() => {
    if (bilanNonInitialise) return;
    form.setFieldsValue({
      nombreBlessesAttendu: totalBlesses || null,
      nombreVictimesAttendu: totalVictimes || null,
      victimesNonIdentifiees: totalVictimes > 0 && victimes.length === 0,
    });
  }, [bilanNonInitialise, totalBlesses, totalVictimes, victimes.length, form]);

  const ouvrir = (editing = null, index = -1) => setModal({ open: true, editing, index });
  const fermer = () => setModal({ open: false, editing: null, index: -1 });

  const save = async (v) => {
    // Déjà saisie à l'étape « Parties adverses » ? Elle compterait deux fois.
    if (modal.index < 0) {
      const deja = memePersonneAutreRole(v, adversaires.map((a) => ({ ...a, estAdversaire: true })));
      if (deja && !(await confirmerDoublonPersonne(deja, v))) return;
    }
    setVictimes((prev) => {
      if (modal.index >= 0) {
        const next = [...prev]; next[modal.index] = v; return next;
      }
      return [...prev, v];
    });
    fermer();
  };

  const remove = (idx) => setVictimes((prev) => prev.filter((_, i) => i !== idx));

  /* ─── Suggestion : sur un sinistre corporel, la personne au volant est la
     victime la plus probable. On la propose, on ne l'impose pas (l'ancienne
     ouverture automatique de la fiche était trop intrusive). ─── */
  const suggestion = useMemo(() => {
    if (suggestionRejetee) return null;
    if (!['CORPOREL', 'MIXTE'].includes(typeDommage)) return null;
    if (victimes.length > 0) return null;

    const v = form.getFieldsValue(true);
    const ficheAssure = (assureNom || '').trim() ? {
      libelle: `${assureNom.trim()} ${(assurePrenom || '').trim()}`.trim(),
      role: conducteurEstAssure !== false ? t('victimes.suggestion.roleAssureAuVolant') : t('victimes.suggestion.roleAssureProprietaire'),
      prefill: {
        nom: assureNom.trim(),
        prenoms: (assurePrenom || '').trim(),
        nationalite: v.assureNationalite || null,
        profession: v.assureProfession || null,
        typeVictime: 'BLESSE',
        typeDommage: 'CORPOREL',
        revenuMensuel: v.revenuMensuel || 0,
      },
    } : null;
    const ficheConducteur = conducteurEstAssure === false && (conducteurNom || '').trim() ? {
      libelle: `${conducteurNom.trim()} ${(conducteurPrenom || '').trim()}`.trim(),
      role: t('victimes.suggestion.roleConducteur'),
      prefill: {
        nom: conducteurNom.trim(),
        prenoms: (conducteurPrenom || '').trim(),
        dateNaissance: v.conducteurDateNaissance
          ? (typeof v.conducteurDateNaissance?.format === 'function'
            ? v.conducteurDateNaissance.format('YYYY-MM-DD')
            : v.conducteurDateNaissance)
          : null,
        typeVictime: 'BLESSE',
        typeDommage: 'CORPOREL',
      },
    } : null;
    /* Conducteur distinct de l'assuré : on propose LES DEUX fiches et l'agent
       choisit. L'ancienne version tranchait seule pour le conducteur - sur un
       cas mixte où c'est l'assuré qu'on indemnise, elle se trompait de personne
       sans jamais poser la question. */
    const choix = [ficheConducteur, ficheAssure].filter(Boolean);
    return choix.length ? { choix } : null;
  }, [
    suggestionRejetee, typeDommage, victimes.length, conducteurEstAssure,
    assureNom, assurePrenom, conducteurNom, conducteurPrenom, form, t,
  ]);

  /* Rapprochement fiches ↔ nombre déclaré. */
  const ecart = totalVictimes > 0 && victimes.length > 0 ? victimes.length - totalVictimes : 0;

  /* Une fiche de plus que le nombre déclaré est une incohérence, pas un détail :
     on bloque l'ajout tant que le nombre déclaré n'a pas été corrigé (avec son
     motif). L'inverse - moins de fiches que déclaré - est le cas normal, les
     identités arrivant avec le PV : on n'y touche pas. */
  const ajoutBloque = totalVictimes > 0 && victimes.length >= totalVictimes;

  const importerFichier = async (file) => {
    try {
      const { victimes: lues, adversaires, erreurs: erreursLecture } = await lireVictimesExcel(file);
      // Les parties adverses ont leur propre étape dans la déclaration : on les signale
      // plutôt que de les mêler aux victimes de l'assuré.
      const erreurs = [
        ...erreursLecture,
        ...adversaires.map((a) => ({ ligne: a._ligne, raison: t('victimes.import.partieAdverse') })),
      ].sort((x, y) => x.ligne - y.ligne);
      setErreursImport(erreurs);
      if (lues.length === 0) {
        toast.error(erreurs.length ? t('victimes.import.aucuneLigne') : t('victimes.import.fichierVide'));
        return;
      }
      setVictimes((prev) => [...prev, ...lues]);
      toast.success(
        erreurs.length
          ? t('victimes.import.fichesImporteesLignesIgnorees', { nombre: lues.length, ignorees: erreurs.length })
          : t('victimes.import.fichesImportees', { nombre: lues.length })
      );
    } catch (e) {
      setErreursImport([]);
      toast.error(t('victimes.import.fichierIllisible'));
    }
  };

  /* En modification, tout changement du bilan annoncé exige un motif -
     exigé aussi par le backend et conservé dans l'historique du dossier. */
  const bilanModifie = bilanInitial && (
    (totalVictimes || null) !== (bilanInitial.total ?? null) ||
    (totalBlesses || null) !== (bilanInitial.blesses ?? null) ||
    (num(decedes) || null) !== (bilanInitial.decedes ?? null)
  );

  return (
    <div style={{ maxWidth: 780 }}>
      <h1 style={H_TITLE}>{t('victimes.titre')}</h1>

      {/* Champs dérivés - envoyés à l'API, jamais saisis à la main. */}
      <Form.Item name="nombreVictimesAttendu" hidden><Input /></Form.Item>
      <Form.Item name="nombreBlessesAttendu" hidden><Input /></Form.Item>
      <Form.Item name="victimesNonIdentifiees" valuePropName="checked" hidden><Switch /></Form.Item>

      {/* ══════ 1. Bilan chiffré ══════ */}
      <Panel
        style={{ marginTop: 26 }}
        icon={<HeartOutlined />}
        title={t('victimes.nombreVictimes')}
      >
        {/* Deux nombres, et le total à côté d'eux plutôt qu'en bandeau sous la
            grille : la ligne se lit d'un coup et le panneau tient en une rangée. */}
        <Grid3>
          <Field name="nombreBlessesAttendu" label={t('victimes.blesses')}>
            <CBInputNumber min={0} max={999} style={{ width: '100%', height: 40 }} placeholder="0" />
          </Field>
          <Field name="nombreDecedesAttendu" label={t('victimes.deces')}>
            <CBInputNumber min={0} max={999} style={{ width: '100%', height: 40 }} placeholder="0" />
          </Field>
          <CelluleTotal total={totalVictimes} deces={num(decedes)} />
        </Grid3>

        {['CORPOREL', 'MIXTE'].includes(typeDommage) && totalVictimes === 0 && (
          <PanelFooterNote tone="warn">
            <Trans
              t={t}
              i18nKey="victimes.sinistreDeclareNature"
              values={{ nature: typeDommage === 'MIXTE' ? t('victimes.natureMixte') : t('victimes.natureCorporel') }}
              components={{ 1: <strong /> }}
            />
          </PanelFooterNote>
        )}

        {bilanModifie && (
          <div style={{ marginTop: 18 }}>
            <Field
              name="motifModificationVictimes"
              label={t('victimes.motifModification')}
              rules={[
                { required: true, message: t('victimes.motifObligatoire') },
                { min: 5, message: t('victimes.cinqCaracteresMinimum') },
              ]}
              hint={t('victimes.conserveHistorique')}
            >
              <Input.TextArea
                rows={2} maxLength={500} showCount
                placeholder={t('victimes.motifExemple')}
                style={{ borderRadius: T.r.md, borderColor: T.line }}
              />
            </Field>
          </div>
        )}
      </Panel>

      {/* ══════ 2. Fiches nominatives ══════ */}
      <Panel
        style={{ marginTop: 20 }}
        icon={<IdcardOutlined />}
        title={t('victimes.fichesVictimes')}
        count={victimes.length}
        action={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Tooltip title={t('victimes.modeleAide')}>
              <Button
                icon={<DownloadOutlined />}
                onClick={telechargerModeleVictimes}
                style={{ height: 34, borderRadius: 9, borderColor: T.brandLine, color: T.brand, fontWeight: 600, fontSize: 13 }}
              >
                {t('victimes.modele')}
              </Button>
            </Tooltip>
            <Upload
              accept=".xlsx,.xls"
              showUploadList={false}
              beforeUpload={(file) => { importerFichier(file); return false; }}
            >
              <Button
                icon={<FileExcelOutlined />}
                style={{ height: 34, borderRadius: 9, borderColor: T.brandLine, color: T.brand, fontWeight: 600, fontSize: 13 }}
              >
                {t('victimes.importer')}
              </Button>
            </Upload>
            {victimes.length > 0 && (
              <Tooltip title={ajoutBloque ? t('victimes.ajoutBloque', { nombre: totalVictimes }) : ''}>
                <span>
                  <Button
                    icon={<IdcardOutlined />}
                    disabled={ajoutBloque}
                    onClick={() => ouvrir()}
                    style={{
                      height: 34, borderRadius: 9, fontWeight: 600, fontSize: 13,
                      ...(ajoutBloque ? {} : { borderColor: T.brandLine, color: T.brand }),
                    }}
                  >
                    {t('commun.ajouter')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
        }
      >
        {suggestion && (
          <SuggestionConducteur
            choix={suggestion.choix}
            onAccept={(c) => ouvrir(c.prefill, -1)}
            onDismiss={() => setSuggestionRejetee(true)}
          />
        )}

        {erreursImport.length > 0 && (
          <Alert
            type="warning"
            showIcon
            closable
            onClose={() => setErreursImport([])}
            style={{ borderRadius: 10, marginBottom: 14 }}
            message={t('victimes.import.lignesNonImportees', { nombre: erreursImport.length })}
            description={
              <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12.5 }}>
                {erreursImport.slice(0, 8).map((e, i) => (
                  <li key={i}>{t('victimes.import.ligneErreur', { ligne: e.ligne, raison: e.raison })}</li>
                ))}
                {erreursImport.length > 8 && <li>{t('victimes.import.autres', { nombre: erreursImport.length - 8 })}</li>}
              </ul>
            }
          />
        )}

        {victimes.length === 0 ? (
          <EmptyState
            icone={<UserOutlined />}
            label={totalVictimes > 0
              ? t('victimes.videDeclarees', { nombre: totalVictimes })
              : t('victimes.videAucune')}
            actionLabel={t('victimes.ajouterVictime')}
            onAction={() => ouvrir()}
          />
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {victimes.map((v, idx) => (
              <VictimeCard
                key={idx}
                victime={v}
                onEdit={() => ouvrir(v, idx)}
                onDelete={() => remove(idx)}
              />
            ))}
          </ul>
        )}

        {/* Rapprochement - et, en cas de dépassement, correction sur place. */}
        {ecart > 0 && (
          <CorrectionNombreDeclare
            key={`${totalVictimes}-${victimes.length}`}
            form={form}
            declare={totalVictimes}
            saisies={victimes.length}
            valeurs={{ blesses: totalBlesses, decedes: num(decedes) }}
          />
        )}
        {ecart < 0 && (
          <PanelFooterNote tone="warn">
            <Trans
              t={t}
              i18nKey="victimes.fichesManquantes"
              values={{ declare: totalVictimes, saisies: victimes.length, manque: -ecart }}
              components={{ 1: <strong />, 3: <strong /> }}
            />
          </PanelFooterNote>
        )}
        {ecart === 0 && totalVictimes > 0 && victimes.length > 0 && (
          <PanelFooterNote tone="ok">
            {t('victimes.fichesConformes', { nombre: victimes.length })}
          </PanelFooterNote>
        )}
      </Panel>

      <RequiredLegend style={{ marginTop: 16 }} />

      <VictimeModal
        open={modal.open}
        onClose={fermer}
        onSave={save}
        initial={modal.editing}
        pays={pays}
      />
    </div>
  );
}

/* ══════════ Correction du nombre déclaré ══════════

   Il y a plus de fiches que de victimes déclarées : c'est une incohérence qui
   doit être tranchée avant d'aller plus loin. Le nombre se corrige ici même,
   avec son motif - le même que celui exigé par le backend et conservé dans
   l'historique du dossier.

   Les compteurs sont en état local, pas en `Form.Item` : les mêmes noms sont
   déjà déclarés dans le panneau du haut, et deux Form.Item de même nom
   cumuleraient leurs règles de validation. « Mettre à jour » écrit dans le
   formulaire en une fois. */
function CorrectionNombreDeclare({ form, declare, saisies, valeurs }) {
  const { t } = useTranslation('declaration');
  // Pré-rempli pour que le geste par défaut résorbe l'écart d'un seul clic.
  const [v, setV] = useState({ ...valeurs, blesses: valeurs.blesses + (saisies - declare) });
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState(null);

  const total = num(v.blesses) + num(v.decedes);
  const set = (cle) => (val) => { setV((p) => ({ ...p, [cle]: val ?? 0 })); setErreur(null); };

  const appliquer = () => {
    if (motif.trim().length < 5) {
      setErreur(t('victimes.correction.motifTropCourt'));
      return;
    }
    form.setFieldsValue({
      nombreBlessesAttendu: num(v.blesses) || null,
      nombreDecedesAttendu: num(v.decedes) || null,
      motifModificationVictimes: motif.trim(),
    });
  };

  const champ = (cle, libelle) => (
    <div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: T.inkSoft, marginBottom: 6 }}>{libelle}</div>
      <CBInputNumber
        min={0} max={999}
        value={v[cle]}
        onChange={set(cle)}
        style={{ width: '100%', height: 40 }}
        placeholder="0"
      />
    </div>
  );

  return (
    <div
      style={{
        marginTop: 14,
        border: `1px solid ${ROW_BORDER}`,
        borderRadius: 11,
        background: ROW_BG,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '11px 16px',
          background: T.warnSoft,
          borderBottom: '1px solid #fed7aa',
          fontSize: 12.5,
          color: T.warn,
          lineHeight: 1.5,
        }}
      >
        <Trans
          t={t}
          i18nKey="victimes.correction.entete"
          values={{ declare, saisies }}
          components={{ 1: <strong />, 3: <strong /> }}
        />
      </div>

      <div style={{ padding: 16 }}>
        <Grid2>
          {champ('blesses', t('victimes.blesses'))}
          {champ('decedes', t('victimes.deces'))}
        </Grid2>

        <div
          style={{
            marginTop: 14, marginBottom: 14,
            fontSize: 12.5,
            color: total === saisies ? T.brandDark : T.warn,
            fontWeight: 600,
          }}
        >
          {total === saisies
            ? t('victimes.correction.nouveauTotalConforme', { total })
            : t('victimes.correction.nouveauTotalEcart', { total, saisies })}
        </div>

        <div style={{ fontSize: 12.5, fontWeight: 600, color: T.inkSoft, marginBottom: 6 }}>
          {t('victimes.correction.motif')}
          <span style={{ color: '#d92d20', fontWeight: 700, marginLeft: 3 }}>*</span>
        </div>
        <Input.TextArea
          rows={2}
          maxLength={500}
          showCount
          value={motif}
          onChange={(e) => { setMotif(e.target.value); setErreur(null); }}
          placeholder={t('victimes.correction.motifExemple')}
          style={{ borderRadius: T.r.md, borderColor: T.line }}
        />
        {erreur && (
          <div style={{ fontSize: 12, color: '#d92d20', marginTop: 6 }}>{erreur}</div>
        )}

        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={appliquer}
          style={{
            marginTop: 14, height: 38, padding: '0 20px', borderRadius: 9,
            background: T.brand, borderColor: T.brand, fontWeight: 600,
          }}
        >
          {t('victimes.correction.mettreAJour')}
        </Button>
      </div>
    </div>
  );
}

/* ══════════ Total du bilan ══════════

   Une cellule de la même grille que les deux compteurs, pas un bandeau sous
   eux : le total est une conséquence de la saisie, il se lit au bout de la
   ligne sans ajouter de hauteur au panneau. Le nombre de blessés n'y est pas
   répété - il est dans le champ d'à côté. */
function CelluleTotal({ total, deces }) {
  const { t } = useTranslation('declaration');
  const vide = total === 0;
  return (
    <div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: T.inkSoft, marginBottom: 6 }}>
        {t('victimes.totalBilan.totalDeclare')}
      </div>
      <div
        style={{
          height: 40, display: 'flex', alignItems: 'center', gap: 8,
          padding: '0 14px', borderRadius: T.r.md,
          background: vide ? T.paper : T.brandSoft,
          border: `1px solid ${vide ? T.line : T.brandLine}`,
        }}
      >
        <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1, color: vide ? T.inkLight : T.brandDark }}>
          {total}
        </span>
        {deces > 0 && (
          <span style={{ fontSize: 12.5, color: T.inkMuted }}>
            <Trans
              t={t}
              i18nKey="victimes.totalBilan.dontDeces"
              values={{ deces }}
              components={{ 1: <strong style={{ color: T.danger }} /> }}
            />
          </span>
        )}
      </div>
    </div>
  );
}

/* ══════════ Suggestion « le conducteur est-il blessé ? » ══════════ */
function SuggestionConducteur({ choix = [], onAccept, onDismiss }) {
  const { t } = useTranslation('declaration');
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '12px 14px', marginBottom: 14,
        borderRadius: 10, background: T.paper, border: `1px solid ${T.line}`,
      }}
    >
      <span style={{ fontSize: 18 }}>💡</span>
      <span style={{ flex: 1, minWidth: 200, fontSize: 12.5, color: T.inkSoft, lineHeight: 1.5 }}>
        {choix.length > 1
          ? t('victimes.suggestion.quiEstLaVictime')
          : (
            <Trans
              t={t}
              i18nKey="victimes.suggestion.creerFicheDe"
              values={{ libelle: choix[0]?.libelle, role: choix[0]?.role }}
              components={{ 1: <strong style={{ color: T.ink }} /> }}
              // Nom saisi par l'agent : échappé puis restitué, sinon un « < » serait lu comme une balise.
              tOptions={{ interpolation: { escapeValue: true } }}
              shouldUnescape
            />
          )}
      </span>
      {choix.map((c) => (
        <Button
          key={c.role}
          size="small"
          onClick={() => onAccept(c)}
          style={{ borderRadius: 8, borderColor: T.brandLine, color: T.brand, fontWeight: 600 }}
        >
          {choix.length > 1 ? `${c.libelle} - ${c.role}` : t('victimes.suggestion.creerFiche')}
        </Button>
      ))}
      <Tooltip title={t('victimes.suggestion.ignorer')}>
        <Button size="small" type="text" icon={<CloseOutlined />} onClick={onDismiss} style={{ color: T.inkLight }} />
      </Tooltip>
    </div>
  );
}

/* ══════════ Carte d'une victime ══════════ */
function VictimeCard({ victime: v, onEdit, onDelete }) {
  const { t } = useTranslation('declaration');
  const cfg = TYPES_VICTIME.find(t => t.value === v.typeVictime)
    || { label: v.typeVictime || t('victimes.carte.neutre'), color: T.inkMuted, bg: T.paper };
  const age = v.dateNaissance ? dayjs().diff(v.dateNaissance, 'year') : null;

  const meta = [
    v.sexe === 'M' ? t('victimes.carte.homme') : v.sexe === 'F' ? t('victimes.carte.femme') : null,
    age != null ? t('victimes.carte.age', { age }) : null,
    v.profession,
  ].filter(Boolean);

  return (
    <li
      style={{
        display: 'flex', alignItems: 'center', gap: 13,
        padding: '13px 14px',
        border: `1px solid ${ROW_BORDER}`,
        borderRadius: 11,
        background: ROW_BG,
      }}
    >
      <span
        style={{
          flexShrink: 0, width: 38, height: 38, borderRadius: 10,
          background: cfg.bg, color: cfg.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 700,
        }}
      >
        {(v.nom || '?').charAt(0).toUpperCase()}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 2 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>
            {v.nom} {v.prenoms}
          </span>
          <span
            style={{
              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
              color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap',
            }}
          >
            {cfg.label}
          </span>
        </div>
        {meta.length > 0 && (
          <div style={{ fontSize: 12, color: T.inkMuted }}>{meta.join(' · ')}</div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
        <Tooltip title={t('commun.modifier')}>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={onEdit} style={{ color: T.inkMuted }} />
        </Tooltip>
        <Popconfirm
          title={t('victimes.carte.retirerConfirmation')}
          onConfirm={onDelete}
          okText={t('commun.retirer')}
          cancelText={t('commun.annuler')}
          okButtonProps={{ danger: true }}
        >
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </div>
    </li>
  );
}
